import type { ThgStationRow } from "../../../types/thgAnnualSummary";

// On-screen formatting only. The CSV writes the raw values with toFixed (see thgCsv.ts).
// The backend already rounds to 3 (kWh) and 2 (EUR) decimals, so formatting at exactly that precision
// shows its figures unchanged: nothing here re-rounds a payout.

const KWH = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const EUR = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COUNT = new Intl.NumberFormat("de-DE");
const BERLIN = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const BERLIN_DATE = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export const formatKwh = (kwh: number): string => KWH.format(kwh);

export const formatEur = (eur: number): string => `${EUR.format(eur)} €`;

export const formatCount = (n: number): string => COUNT.format(n);

/** An instant in Berlin time, whatever the reader's zone: the backend closes years in Berlin time. */
export const formatBerlin = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? String(iso) : BERLIN.format(date);
};

/** The Berlin calendar date of an instant, e.g. when a credit note was generated. */
export const formatBerlinDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? String(iso) : BERLIN_DATE.format(date);
};

/**
 * Free-charge energy: total minus paid. The only figure the page derives.
 *
 * Computed in whole milli-kWh, so `4100 - 3831.394` is exactly 268.606 rather than 268.60599999…,
 * which would otherwise survive into a display rounded one digit further.
 */
export const freeEnergyKwh = (totalKwh: number, paidKwh: number): number =>
  (Math.round(totalKwh * 1000) - Math.round(paidKwh * 1000)) / 1000;

export type OwnerState = "OK" | "NONE" | "RECORD_MISSING";

/**
 * Whether a station can be paid.
 * NONE: no customer assigned. RECORD_MISSING: the assigned customer no longer exists.
 * An absent `owner` key is treated as NONE: a backend without the owner fields has told us nothing.
 */
export const ownerState = (row: Pick<ThgStationRow, "owner">): OwnerState => {
  if (!row?.owner) return "NONE";
  return row.owner.name ? "OK" : "RECORD_MISSING";
};
